import React, { useMemo } from "react";
import { Button } from "@mui/material";

interface LinkButtonProps {
  text: string;
  href?: string;
  external?: boolean;

  /** ação (ex: abrir drawer / navegar via react-router por fora) */
  handleClickDrawer?: () => void;

  fontColor?: string;
  fontHoverColor?: string;

  /** SEO/A11y */
  title?: string;
  ariaLabel?: string;
  ariaCurrent?: "page" | "step" | "location" | "date" | "time" | true | false;
  id?: string;
}

const ButtonLink: React.FC<LinkButtonProps> = ({
  text,
  href,
  external = false,
  handleClickDrawer,
  fontColor,
  title,
  ariaLabel,
  ariaCurrent,
  fontHoverColor,
  id,
}) => {
  // Decide se deve renderizar como link real (melhor p/ SEO) ou botão de ação
  const isLink = Boolean(href) && !handleClickDrawer;

  // Props para anchor quando for link
  const anchorProps = useMemo(() => {
    if (!isLink || !href) return {};

    if (external) {
      return {
        href,
        target: "_blank",
        rel: "noopener noreferrer",
      } as const;
    }

    return { href } as const;
  }, [external, href, isLink]);

  return (
    <Button
      id={id}
      // ✅ A11y/SEO
      title={title ?? text}
      aria-label={ariaLabel ?? title ?? text}
      aria-current={ariaCurrent}
      // ✅ Link real quando tiver href e não for ação custom
      component={isLink ? "a" : "button"}
      {...anchorProps}
      // ✅ Click: se for ação custom, chama; se for link, deixa o navegador cuidar
      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
        if (handleClickDrawer) {
          // evita comportamento inesperado se alguém passar href + handler no futuro
          e.preventDefault?.();
          handleClickDrawer();
        }
      }}
      sx={{
        color: fontColor ? fontColor : "white",
        fontSize: "22px",
        fontFamily: "PFDinTextCompPro-Medium, Arial, sans-serif",
        fontWeight: "700",
        textTransform: "none",
        backgroundColor: "transparent",
        "&:hover": { color: fontHoverColor || "#FFDF22", backgroundColor: "transparent" },
        "&:active": { backgroundColor: "transparent", height: "95%" },

        /**
         * ⚠️ Não remova foco completamente.
         * SEO não liga, mas A11y sim. Melhor manter um foco discreto.
         * Se você quiser “invisível”, use opacity baixo, mas não "none".
         */
        "&:focus-visible": {
          outline: "2px solid rgba(255,255,255,0.75)",
          outlineOffset: 2,
        },

        boxShadow: "none",
        "&:before": { content: "none" },
        "&:after": { content: "none" },
      }}
    >
      {text}
    </Button>
  );
};

export default ButtonLink;
